import { OptionWithRank } from '@playbooks/action-cards-utils/src/types'

import { InputProps } from '../types'
import { useAppContext } from '../../../contexts/AppContext'
import { ComboboxContentWithContainer } from '../../../components/ui/ComboboxContentWithContainer'
import { Combobox, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@playbooks/ui/components/combobox'
import { useTranslation } from '../../../contexts/localization/Localization'

/**
 * A dropdown component for selecting an option from a list of options.
 *
 * @param {InputProps} props - The properties for the dropdown component.
 * @param {QuestionData} props.question - The question data containing the options and the current value.
 * @param {function} props.onChange - The callback function to handle changes in the selected option.
 * @returns {JSX.Element} The rendered input component.
 */
export default function Dropdown({ question, onChange }: InputProps) {
  const { text } = useTranslation()
  const { rootRef } = useAppContext()

  const options: OptionWithRank[] =
    question.data?.map(option => ({
      id: option.id,
      name: option.name,
      rank: option.rank,
    })) || []

  const selectedOption = options.find(option => option.id === question.value)

  return (
    <Combobox
      items={options}
      value={selectedOption?.name || ''}
      onValueChange={value => {
        if (!value) {
          onChange({ id: null })
        }
      }}
    >
      <ComboboxInput
        data-testid="dropdown-input"
        placeholder={text('dropdownPlaceholder')}
        showClear={!!selectedOption}
        className="w-full"
      />

      <ComboboxContentWithContainer container={rootRef}>
        <ComboboxEmpty>{text('dropdownNoItemsFound')}</ComboboxEmpty>

        <ComboboxList>
          {(item: OptionWithRank) => (
            <ComboboxItem key={item.id} className="font-normal" value={item.name} onClick={() => onChange(item)}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContentWithContainer>
    </Combobox>
  )
}
